import { createTheme } from "@mui/material/styles";

const SYSTEM_FONT_STACK = [
  "-apple-system",
  "BlinkMacSystemFont",
  '"Segoe UI"',
  "Roboto",
  "Oxygen",
  "Ubuntu",
  "Cantarell",
  '"Helvetica Neue"',
  "sans-serif"
].join(", ");

const sharedTypography = {
  fontFamily: SYSTEM_FONT_STACK,
  fontSize: 15,
  h1: { fontWeight: 700, letterSpacing: "-0.025em", lineHeight: 1.15 },
  h2: { fontWeight: 600, letterSpacing: "-0.02em" },
  h3: { fontWeight: 600, letterSpacing: "-0.015em" },
  h4: { fontWeight: 600, letterSpacing: "-0.01em" },
  h5: { fontWeight: 600 },
  h6: { fontWeight: 600 },
  button: { textTransform: "none", fontWeight: 600, letterSpacing: "-0.005em" }
};

const sharedShape = { borderRadius: 8 };

const sharedComponents = (mode) => ({
  MuiButton: {
    defaultProps: { disableElevation: true },
    styleOverrides: {
      root: {
        borderRadius: 8,
        paddingInline: 16,
        paddingBlock: 8,
        transition: "transform 0.15s ease, box-shadow 0.15s ease, background-color 0.15s ease"
      },
      contained: {
        "&:hover": {
          transform: "translateY(-1px)",
          boxShadow: mode === "dark"
            ? "0 4px 12px rgba(0, 0, 0, 0.5)"
            : "0 4px 12px rgba(0, 0, 0, 0.12)"
        }
      }
    }
  },
  MuiPaper: {
    styleOverrides: {
      root: { backgroundImage: "none" }
    }
  },
  MuiAppBar: {
    defaultProps: { elevation: 0 },
    styleOverrides: {
      root: {
        backgroundColor: mode === "dark" ? "rgba(10, 10, 10, 0.85)" : "rgba(255, 255, 255, 0.85)",
        color: mode === "dark" ? "#fafafa" : "#0a0a0a",
        borderBottom: `1px solid ${mode === "dark" ? "#262626" : "#e5e5e5"}`,
        backdropFilter: "saturate(180%) blur(12px)",
        WebkitBackdropFilter: "saturate(180%) blur(12px)"
      }
    }
  },
  MuiTextField: {
    defaultProps: { variant: "outlined", fullWidth: true, size: "medium" }
  },
  MuiOutlinedInput: {
    styleOverrides: {
      root: { borderRadius: 8 },
      input: {
        "&:-webkit-autofill, &:-webkit-autofill:hover, &:-webkit-autofill:focus, &:-webkit-autofill:active": {
          WebkitBoxShadow: `0 0 0 1000px ${mode === "dark" ? "#141414" : "#ffffff"} inset`,
          WebkitTextFillColor: mode === "dark" ? "#fafafa" : "#0a0a0a",
          caretColor: mode === "dark" ? "#fafafa" : "#0a0a0a",
          borderRadius: "inherit",
          transition: "background-color 99999s ease-in-out 0s"
        }
      }
    }
  },
  MuiCard: {
    defaultProps: { variant: "outlined" },
    styleOverrides: {
      root: {
        borderRadius: 12,
        borderColor: mode === "dark" ? "#262626" : "#e5e5e5"
      }
    }
  },
  MuiAlert: {
    styleOverrides: {
      root: { borderRadius: 8 }
    }
  },
  MuiTableHead: {
    styleOverrides: {
      root: {
        "& .MuiTableCell-head": {
          fontWeight: 600,
          backgroundColor: mode === "dark" ? "#1a1a1a" : "#f5f5f5",
          color: mode === "dark" ? "#fafafa" : "#0a0a0a"
        }
      }
    }
  },
  MuiTableCell: {
    styleOverrides: {
      root: {
        borderColor: mode === "dark" ? "#262626" : "#e5e5e5"
      }
    }
  },
  MuiLink: {
    defaultProps: { underline: "hover" }
  }
});

export const buildTheme = (mode) =>
  createTheme({
    palette: {
      mode,
      ...(mode === "light"
        ? {
            primary: {
              main: "#0a0a0a",
              contrastText: "#ffffff"
            },
            secondary: {
              main: "#2563eb",
              contrastText: "#ffffff"
            },
            background: {
              default: "#ffffff",
              paper: "#ffffff"
            },
            divider: "#e5e5e5",
            text: {
              primary: "#0a0a0a",
              secondary: "#525252"
            }
          }
        : {
            primary: {
              main: "#fafafa",
              contrastText: "#0a0a0a"
            },
            secondary: {
              main: "#60a5fa",
              contrastText: "#0a0a0a"
            },
            background: {
              default: "#0a0a0a",
              paper: "#141414"
            },
            divider: "#262626",
            text: {
              primary: "#fafafa",
              secondary: "#a3a3a3"
            }
          })
    },
    typography: sharedTypography,
    shape: sharedShape,
    components: sharedComponents(mode)
  });
